import {mapKpiPerformanceToPlantStatusKpis, type KpiData} from '@domain/kpis'
import {ApiClient} from '@hconnect/apiclient'
import {KpiPerformanceDto} from '@hconnect/common/types'
import {spawnErrorToast} from '@hm/fundamental'
import {useQuery, UseQueryOptions} from '@tanstack/react-query'
import {AxiosError} from 'axios'
import {Moment} from 'moment-timezone'
import {useEffect} from 'react'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../consts'
const QueryKey = 'optimization-specialist-kpis'

type OptimizationSpecialistKpisSearchParams = {
  plantId: string
  from?: Moment
  to?: Moment
}

export const useOptimizationSpecialistKpis = <TSelect = KpiData[]>(
  {plantId, from, to}: OptimizationSpecialistKpisSearchParams,
  options?: Omit<UseQueryOptions<KpiPerformanceDto, AxiosError, TSelect>, 'queryKey' | 'queryFn'>
) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)

  const startDate = from?.toISOString()
  const endDate = to?.toISOString()

  const query = useQuery<KpiPerformanceDto, AxiosError, TSelect>({
    queryKey: [QueryKey, plantId, startDate, endDate],
    queryFn: async ({signal}) => {
      const path = `/kpi-result/${plantId}/equipment-performances/optimization-specialist`
      const response = await ApiClient.axiosInstance.get<KpiPerformanceDto>(path, {
        params: {startDate, endDate},
        signal
      })

      return response.data
    },
    enabled: !!from && !!to,
    select: mapKpiPerformanceToPlantStatusKpis as unknown as (
      data: KpiPerformanceDto | undefined
    ) => TSelect,
    ...options
  })

  useEffect(() => {
    if (query.error) {
      spawnErrorToast(t('plantStatus.message.failedToFetchKpis'), {closeOnClick: true})
    }
  }, [query.error, t])

  return query
}
